import { useState } from 'react';
import type { LootKind, VocationId } from '@estandarte/engine';
import { portraitOf } from '../art';
import { ArtBackdrop, CATEGORY_BG, LootArt, RegionArt, WildArt } from '../art/illustrations';
import { useUi } from '../store/ui';
import { icons, symbolIcon } from '../art';
import { cardSymbol, regionName, S, territoryName, theme } from '../theme';

type Deck = 'territory' | 'mission' | 'loot' | 'boss';

const CATEGORY: Record<string, string> = {
  attack: 'Ataque',
  defense: 'Defesa',
  control: 'Controle',
  movement: 'Movimento',
  reinforce: 'Reforço',
  cards: 'Cartas',
};

/** Verso de carta: território azul com rosa dos ventos, missão creme com envelope lacrado, loot dourado, boss escuro. */
export function CardBack({ deck, mini }: { deck: Deck; mini?: boolean }) {
  return (
    <span className={`pcard back back-${deck}${mini ? ' mini' : ''}`} aria-hidden>
      <svg viewBox="0 0 40 56" className="back-art">
        {deck === 'territory' && (
          <g fill="none" stroke="currentColor" strokeWidth="1.2">
            <circle cx="20" cy="28" r="11" />
            <path d="M20 12 L23 28 L20 44 L17 28 Z" fill="currentColor" fillOpacity=".35" />
            <path d="M4 28 L20 25 L36 28 L20 31 Z" />
          </g>
        )}
        {deck === 'mission' && (
          <g stroke="currentColor" strokeWidth="1.2" fill="none">
            <rect x="7" y="17" width="26" height="20" rx="1.5" />
            <path d="M7 17 L20 29 L33 17" />
            <circle cx="20" cy="30" r="4.2" fill="#A23B3B" stroke="#7d2c2c" />
          </g>
        )}
        {deck === 'loot' && (
          <g stroke="currentColor" strokeWidth="1.2" fill="none">
            <path d="M20 13 L24 24 L35 28 L24 32 L20 43 L16 32 L5 28 L16 24 Z" fill="currentColor" fillOpacity=".25" />
          </g>
        )}
        {deck === 'boss' && (
          <g stroke="currentColor" strokeWidth="1.3" fill="none">
            <path d="M13 22 C9 17 10 12 14 10 C13 14 14 17 17 19 M27 22 C31 17 30 12 26 10 C27 14 26 17 23 19" />
            <circle cx="20" cy="27" r="7" fill="currentColor" fillOpacity=".3" />
          </g>
        )}
      </svg>
    </span>
  );
}

type CardSize = 'sm' | 'md' | 'lg';

/** O que uma carta mostra; serve tanto para a carta na mão quanto para a ampliada. */
export type CardDesc = { deck: 'territory'; card: string } | { deck: 'loot'; kind: LootKind } | { deck: 'vocation'; vocation: VocationId };

const TAG_COLOR: Record<string, string> = {
  attack: '#a23b3b',
  defense: '#2f5f86',
  control: '#6a4b9a',
  movement: '#2a7470',
  reinforce: '#9a7420',
  cards: '#3f7448',
};

/** Faixa da região nas cartas de território (versões escuras das cores do mapa, para o texto claro ler bem). */
const REGION_TAG: Record<string, string> = {
  north: '#4b7444',
  south: '#857a35',
  ice: '#4a7090',
  isles: '#2c7472',
  darama: '#a06e26',
  far: '#67518e',
};

let uidSeq = 0;

/** Carta no estilo das referências: arte grande em cima, título, faixa de tipo, moeda e o efeito em destaque. */
/** Cor de cada vocação nas cartas (fundo da arte e faixa). */
const VOCATION_BG: Record<VocationId, [string, string, string]> = {
  defense: ['#1a2230', '#46566e', '#cfe0f5'],
  range: ['#1d2a14', '#4f6a33', '#e4f5b0'],
  damage: ['#2a0f18', '#6e2328', '#ffb08a'],
  heal: ['#0f2a24', '#2c6a5a', '#b8f5dc'],
  harmony: ['#2a1a0c', '#8a5a22', '#ffd99a'],
};
const VOCATION_TAG: Record<VocationId, string> = { defense: '#46566e', range: '#4f6a33', damage: '#8a3b2a', heal: '#2c6a5a', harmony: '#9a5a1a' };

function CardFace({ desc, size, uid }: { desc: CardDesc; size: CardSize; uid: string }) {
  if (desc.deck === 'vocation') {
    const v = theme.vocations[desc.vocation];
    return (
      <>
        <span className="gcard-art">
          <svg viewBox="0 0 120 90" preserveAspectRatio="xMidYMid slice" aria-hidden>
            <ArtBackdrop id={`voc-${desc.vocation}`} palette={VOCATION_BG[desc.vocation]} />
            <ellipse cx={60} cy={80} rx={34} ry={5} fill="#000" opacity={0.25} />
            <image href={portraitOf(desc.vocation)} x={6} y={14} width={108} height={70} preserveAspectRatio="xMidYMid meet" />
          </svg>
          <Edge />
          <span className="gcard-title">{v.name}</span>
          <span className="gcard-tag" style={{ background: VOCATION_TAG[desc.vocation] }}>
            {v.role.charAt(0).toUpperCase() + v.role.slice(1)}
          </span>
        </span>
        <span className="gcard-body">
          <span className="gcard-headline" style={{ color: VOCATION_TAG[desc.vocation] }}>
            {v.headline}
          </span>
          {size !== 'sm' && <span className="gcard-text">{v.text}</span>}
        </span>
      </>
    );
  }
  if (desc.deck === 'loot') {
    const def = theme.loot[desc.kind];
    const cat = def.category;
    const reaction = def.timing === 'any-time';
    return (
      <>
        <span className="gcard-art">
          <svg viewBox="0 0 120 90" preserveAspectRatio="xMidYMid slice" aria-hidden>
            <ArtBackdrop id={`${cat}`} palette={CATEGORY_BG[cat] ?? CATEGORY_BG.attack} />
            <LootArt kind={desc.kind} />
          </svg>
          <Edge />
          <span className="gcard-title">{def.name}</span>
          <span className="gcard-tag" style={{ background: TAG_COLOR[cat] }}>
            {reaction ? 'Reação' : CATEGORY[cat] ?? cat}
          </span>
        </span>
        <span className="gcard-body">
          <span className="gcard-headline" style={{ color: TAG_COLOR[cat] }}>
            {def.headline}
          </span>
          {size !== 'sm' && <span className="gcard-text">{def.text}</span>}
        </span>
      </>
    );
  }
  const sym = cardSymbol(desc.card);
  const wild = sym === 'wild';
  const t = theme.territories[desc.card];
  const region = t?.region;
  return (
    <>
      <span className="gcard-art">
        <svg viewBox="0 0 120 90" preserveAspectRatio="xMidYMid slice" aria-hidden>
          {wild ? <WildArt uid={uid} /> : <RegionArt region={region} uid={uid} />}
        </svg>
        <Edge />
        <span className="gcard-title">{territoryName(desc.card)}</span>
        <span className="gcard-coin" title={S.symbols[sym]}>
          <img src={symbolIcon(sym)} alt="" />
        </span>
        <span className="gcard-tag" style={{ background: wild ? '#7a5a32' : REGION_TAG[region] ?? '#585c6e' }}>
          {wild ? 'Curinga' : regionName(region)}
        </span>
      </span>
      <span className="gcard-body">
        <span className="gcard-headline">{S.symbols[sym]}</span>
        {size !== 'sm' && (
          <span className="gcard-text">{wild ? 'Vale qualquer símbolo numa troca (no máximo 2 por troca).' : t?.description}</span>
        )}
      </span>
    </>
  );
}

/** Borda ondulada entre a arte e a caixa de texto. */
function Edge() {
  return (
    <svg className="gcard-edge" viewBox="0 0 120 8" preserveAspectRatio="none" aria-hidden>
      <path d="M0,8 V4 C8,1 14,6 22,3.5 C30,1 36,5.5 46,3 C56,0.5 62,5 72,3 C82,1 88,6 98,3.5 C106,1.5 112,4.5 120,3 V8 Z" fill="#fbf8ef" />
      <path d="M0,4 C8,1 14,6 22,3.5 C30,1 36,5.5 46,3 C56,0.5 62,5 72,3 C82,1 88,6 98,3.5 C106,1.5 112,4.5 120,3" fill="none" stroke="#1d1f30" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

function useInspect(desc: CardDesc) {
  const setInspect = useUi((s) => s.setInspect);
  return {
    onPointerEnter: (e: React.PointerEvent<HTMLElement>) => {
      if (e.pointerType !== 'mouse') return;
      setInspect({ desc, rect: e.currentTarget.getBoundingClientRect() });
    },
    onPointerLeave: () => setInspect(null),
  };
}

export function GameCard({
  desc,
  size = 'sm',
  selected,
  disabled,
  onClick,
  actionLabel,
  title,
  inspect = true,
}: {
  desc: CardDesc;
  size?: CardSize;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  actionLabel?: string;
  title?: string;
  inspect?: boolean;
}) {
  const [uid] = useState(() => `c${++uidSeq}`);
  const hover = useInspect(desc);
  const Tag = onClick ? 'button' : 'div';
  const cat = desc.deck === 'loot' ? theme.loot[desc.kind].category : desc.deck;
  return (
    <Tag
      className={`gcard size-${size} deck-${desc.deck} cat-${cat}${selected ? ' on' : ''}${onClick ? ' clickable' : ''}`}
      onClick={onClick}
      disabled={onClick ? disabled : undefined}
      aria-pressed={onClick && selected !== undefined ? selected : undefined}
      title={title}
      {...(inspect && size === 'sm' ? hover : {})}
    >
      <CardFace desc={desc} size={size} uid={uid} />
      {actionLabel && <span className="gcard-action">{actionLabel}</span>}
    </Tag>
  );
}

export function TerritoryCard({
  card,
  selected,
  disabled,
  onClick,
}: {
  card: string;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <GameCard
      desc={{ deck: 'territory', card }}
      selected={selected}
      disabled={disabled}
      onClick={onClick}
      title={`${territoryName(card)} — ${S.symbols[cardSymbol(card)]}`}
    />
  );
}

export function LootCard({
  kind,
  onClick,
  disabled,
  actionLabel,
  size = 'sm',
}: {
  kind: LootKind;
  onClick?: () => void;
  disabled?: boolean;
  actionLabel?: string;
  size?: CardSize;
}) {
  return <GameCard desc={{ deck: 'loot', kind }} size={size} onClick={onClick} disabled={disabled} actionLabel={actionLabel} />;
}

/** Carta ampliada ao passar o mouse numa carta pequena (fica fora dos painéis, sem ser cortada). */
export function CardInspector() {
  const inspect = useUi((s) => s.inspect);
  const [uid] = useState(() => `insp${++uidSeq}`);
  if (!inspect) return null;
  const W = 17 * 11.6;
  const H = 17 * 16.2;
  const r = inspect.rect;
  let left = r.right + 14;
  if (left + W > window.innerWidth - 8) left = r.left - W - 14;
  if (left < 8) left = Math.min(Math.max(8, r.left + r.width / 2 - W / 2), window.innerWidth - W - 8);
  const top = Math.min(Math.max(8, r.top + r.height / 2 - H / 2), window.innerHeight - H - 8);
  const cat = inspect.desc.deck === 'loot' ? theme.loot[inspect.desc.kind].category : inspect.desc.deck;
  return (
    <div className={`gcard size-lg inspector deck-${inspect.desc.deck} cat-${cat}`} style={{ left, top }} aria-hidden>
      <CardFace desc={inspect.desc} size="lg" uid={uid} />
    </div>
  );
}

export { icons };
